import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';

export interface SettingOption {
  value: string | number;
  label: string;
}

interface SettingSelectProps {
  apiBaseUrl: string;
  settingKey: string;
  title: string;
  description?: string;
  options: SettingOption[];
  defaultValue: string | number;
  saveDescription?: string;
  normalizeValue?: (raw: unknown) => string | number;
}

// A dropdown bound to a persisted server setting (Setting key/value store).
// Reads the current value on mount and saves immediately on change.
const SettingSelect: React.FC<SettingSelectProps> = ({
  apiBaseUrl,
  settingKey,
  title,
  description,
  options,
  defaultValue,
  saveDescription,
  normalizeValue,
}) => {
  const [value, setValue] = useState<string | number>(defaultValue);
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);
  const [saved, setSaved] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const fetchSetting = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiBaseUrl}/settings/${settingKey}`, {
          withCredentials: true,
        });
        if (active && response.data?.value !== undefined && response.data?.value !== null) {
          const next = normalizeValue
            ? normalizeValue(response.data.value)
            : response.data.value;
          setValue(next);
        }
        if (active) setError(null);
      } catch (err: any) {
        if (err.response?.status === 404) {
          if (active) setValue(defaultValue);
        } else if (active) {
          console.error(`Error fetching setting ${settingKey}:`, err);
          setError('Failed to load setting');
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchSetting();
    return () => {
      active = false;
    };
  }, [apiBaseUrl, settingKey, defaultValue, normalizeValue]);

  const handleChange = async (raw: string) => {
    const option = options.find((o) => String(o.value) === raw);
    if (!option) return;

    setUpdating(true);
    setSaved(false);
    try {
      const response = await axios.put(
        `${apiBaseUrl}/settings/${settingKey}`,
        { value: option.value, description: saveDescription || title },
        { withCredentials: true }
      );
      setValue(response.data?.value ?? option.value);
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      console.error(`Error updating setting ${settingKey}:`, err);
      setError('Failed to update setting');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div>
      <label
        className="form-check-label"
        htmlFor={`setting-${settingKey}`}
        style={{ fontSize: '1.1rem', fontWeight: 500 }}
      >
        {title}
      </label>
      {description && <div className="text-muted small mt-1">{description}</div>}
      <div className="d-flex align-items-center gap-2 mt-2">
        <select
          id={`setting-${settingKey}`}
          className="form-select"
          style={{ maxWidth: '22rem' }}
          value={String(value)}
          onChange={(e) => handleChange(e.target.value)}
          disabled={loading || updating}
        >
          {options.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>
              {o.label}
            </option>
          ))}
        </select>
        {(loading || updating) && <FontAwesomeIcon icon={faSync} spin className="text-muted" />}
        {saved && !updating && <span className="text-success small">Saved</span>}
      </div>
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

export default SettingSelect;
